import { get } from 'lodash';
import { DestinationConfig, PageOption, PrefilledContent, TranslateFn, EventOption } from './types.ts';

const normalizeBooleanLike = (value: any): boolean | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return !!value;
};

const getBooleanConfigFlag = (
    prefilledContent: PrefilledContent | null | undefined,
    pascalCaseKey: keyof PrefilledContent,
    camelCaseKey: keyof PrefilledContent
): boolean | null => {
    const pascalCaseValue = get(prefilledContent, pascalCaseKey as string, null);
    const normalizedPascal = normalizeBooleanLike(pascalCaseValue);
    if (normalizedPascal !== null) {
        return normalizedPascal;
    }

    const camelCaseValue = get(prefilledContent, camelCaseKey as string, null);
    const normalizedCamel = normalizeBooleanLike(camelCaseValue);
    if (normalizedCamel !== null) {
        return normalizedCamel;
    }

    return null;
};

export const mapFeaturedContentType = (type: any): string | null => {
    if (type === null || type === undefined) return null;
    const normalized = Number(type);
    const typeMap: Record<number, string> = {
        1: 'event',
        2: 'project',
        3: 'job',
    };
    if (!Number.isNaN(normalized) && typeMap[normalized]) {
        return typeMap[normalized];
    }
    if (typeof type === 'string') {
        const lower = type.toLowerCase();
        if (['event', 'project', 'job'].includes(lower)) {
            return lower;
        }
    }
    return null;
};

export const mapPageToSelectItem = (page: any, translate?: TranslateFn): PageOption => {
    const sourcePage = get(page, 'object', get(page, 'Object', page));
    const imageList = get(sourcePage, 'ImageURL', get(sourcePage, 'Images', []));
    const published = normalizeBooleanLike(get(sourcePage, 'Published', get(sourcePage, 'published', null)));
    const visibility = get(sourcePage, 'Visibility', get(sourcePage, 'visibility', null));

    // Safely resolve translated fallback without relying on bound `this`
    let translatedFallback = null;
    try {
        translatedFallback = translate ? translate('labels.page') : null;
    } catch (err) {
        translatedFallback = null;
    }

    const nameFallback = translatedFallback || 'Page';

    return {
        Name: get(sourcePage, 'Title', get(sourcePage, 'Name', get(sourcePage, 'Value', nameFallback))),
        Value: get(sourcePage, 'ID', get(sourcePage, 'id', null)),
        Disabled: get(sourcePage, 'Disabled', get(sourcePage, 'disabled', false)) || false,
        ShowThumbnail: true,
        RoundThumbnail: false,
        ThumbnailImageURL:
            Array.isArray(imageList) && imageList.length > 0
                ? imageList[0]?.ImageURL || imageList[0]?.url || imageList[0]
                : null,
        Paid: !!normalizeBooleanLike(get(sourcePage, 'Paid', get(sourcePage, 'paid', false))),
        Private: !!normalizeBooleanLike(get(sourcePage, 'Private', get(sourcePage, 'private', false))),
        Published: published,
        Slug: get(sourcePage, 'Slug', get(sourcePage, 'slug', null)),
        Visibility: visibility,
    };
};

const getThumbnailImageURL = (imageURL: string[] | string | null | undefined): string | null => {
    if (Array.isArray(imageURL)) {
        return imageURL.length > 0 ? imageURL[0] : null;
    }
    return imageURL || null;
};

const getTranslatedLabel = (translate: TranslateFn | undefined, key: string, fallback: string) => {
    try {
        return translate ? translate(key) : fallback;
    } catch (err) {
        return fallback;
    }
};

const normalizeParentType = (parentType: any): number | null => {
    if (parentType === null || parentType === undefined || parentType === '') return null;
    const normalized = Number(parentType);
    if (!Number.isNaN(normalized)) return normalized;

    const parentTypeMap: Record<string, number> = {
        event: 1,
        project: 2,
        page: 2,
        job: 3,
        post: 0,
    };
    if (typeof parentType === 'string') {
        return parentTypeMap[parentType.toLowerCase()] ?? null;
    }
    return null;
};

export const mapPrefilledPageOption = (
    prefilledContent: PrefilledContent,
    translate?: TranslateFn
): PageOption | null => {
    const parentID = get(prefilledContent, 'ParentID', null);
    if (parentID === null || parentID === undefined) return null;

    return {
        Name: get(prefilledContent, 'Title', getTranslatedLabel(translate, 'labels.page', 'Page')),
        Value: parentID,
        Disabled: false,
        ShowThumbnail: true,
        RoundThumbnail: false,
        ThumbnailImageURL: getThumbnailImageURL(get(prefilledContent, 'ImageURL', null)),
        Slug: get(prefilledContent, 'Slug', null),
    };
};

export const mapPrefilledEventOption = (
    prefilledContent: PrefilledContent,
    translate?: TranslateFn
): EventOption | null => {
    const parentID = get(prefilledContent, 'ParentID', null);
    if (parentID === null || parentID === undefined) return null;

    return {
        Name: get(prefilledContent, 'Title', getTranslatedLabel(translate, 'labels.event', 'Event')),
        Value: `event-${parentID}`,
        Disabled: false,
        __type: 'event',
        EventID: parentID,
        ShowThumbnail: true,
        RoundThumbnail: false,
        ThumbnailImageURL: getThumbnailImageURL(get(prefilledContent, 'ImageURL', null)),
    };
};

export const shouldShowFeed = (feedDisplay: any, feedAllowed: boolean) => {
    if (feedDisplay === 1) return false;
    if (feedDisplay === 2) return true;
    return feedAllowed;
};

export const isPagePrivateOrUnpublished = (pageItem: any) => {
    const isPaidFlag = !!normalizeBooleanLike(get(pageItem, 'Paid', get(pageItem, 'paid', false)));
    const isPrivateFlag = !!normalizeBooleanLike(get(pageItem, 'Private', get(pageItem, 'private', false)));
    const published = normalizeBooleanLike(get(pageItem, 'Published', get(pageItem, 'published', null)));
    const visibility = get(pageItem, 'Visibility', get(pageItem, 'visibility', null));
    const publishedStatus = published === null || published === undefined ? false : !!published;
    return (
        !publishedStatus ||
        isPaidFlag ||
        isPrivateFlag ||
        visibility === 0 ||
        visibility === false ||
        visibility === '0' ||
        visibility === 'false'
    );
};

export const isEventItem = (item: any) => {
    const value = get(item, 'Value', null);
    return get(item, '__type', null) === 'event' || (typeof value === 'string' && value.startsWith('event-'));
};

export const getFeedDisplayForSelection = (
    selectedDestination: PageOption | EventOption | null,
    feedSelected: boolean,
    useImplicitPageRules = true
): number => {
    if (selectedDestination && isEventItem(selectedDestination)) {
        return feedSelected ? 2 : 1;
    }
    if (selectedDestination) {
        const isPrivate = useImplicitPageRules && isPagePrivateOrUnpublished(selectedDestination);
        if (isPrivate) return 1;
        return feedSelected ? 2 : 1;
    }
    return feedSelected ? 2 : 0;
};

export const getInitialFeedSelection = (feedDisplay: any, hasParentSelection: boolean): boolean => {
    if (feedDisplay === 1) return false;
    if (feedDisplay === 2) return true;
    return !hasParentSelection;
};

// getDestinationConfig
// - Normalizes prefilled destination data used by the editor
// - Consumer-defined destination exists only when a concrete parent is provided
// - FeedDisplay alone only defines the initial feed selection; it does not disable search-driven destination rules
export const getDestinationConfig = (
    prefilledContent: PrefilledContent | null | undefined,
    translate?: TranslateFn
): DestinationConfig => {
    const parentID = get(prefilledContent, 'ParentID', null);
    const parentType = normalizeParentType(get(prefilledContent, 'ParentType', null));
    const feedDisplay = get(prefilledContent, 'FeedDisplay', null);
    const lockParentFlag = getBooleanConfigFlag(prefilledContent, 'LockParentDestination', 'lockParentDestination');
    const lockFeedFlag = getBooleanConfigFlag(prefilledContent, 'LockFeedDestination', 'lockFeedDestination');
    const disablePageSearchFlag = getBooleanConfigFlag(prefilledContent, 'DisablePageSearch', 'disablePageSearch');
    const hasParent = parentID !== null && parentID !== undefined && parentType !== null && parentType !== undefined;
    const page =
        parentType === 2 && hasParent ? mapPrefilledPageOption(prefilledContent as PrefilledContent, translate) : null;
    const event =
        parentType === 1 && hasParent ? mapPrefilledEventOption(prefilledContent as PrefilledContent, translate) : null;
    const hasDefinedDestination = hasParent;

    const lockParentDestination = lockParentFlag !== null && lockParentFlag !== undefined ? !!lockParentFlag : false;
    const lockFeedDestination = lockFeedFlag !== null && lockFeedFlag !== undefined ? !!lockFeedFlag : false;
    const disablePageSearch =
        disablePageSearchFlag !== null && disablePageSearchFlag !== undefined ? !!disablePageSearchFlag : false;

    return {
        parentID,
        parentType,
        feedDisplay,
        hasDefinedDestination,
        page,
        event,
        lockParentDestination,
        lockFeedDestination,
        disablePageSearch,
    };
};
